/**
 * Ultrasound Service
 * 
 * Service for fetching ultrasound baseline data. Handles baseline recommendations, 
 * baseline data, and WAV file URL generation with comprehensive error handling.
 * 
 * Note: Caching is currently disabled to ensure fresh data on every request.
 */

import { apiClient, ApiError, ApiErrorType } from '../api';
import {
    UltsBaseline,
    UltsBaselineRecommendation,
    UltsSoundFiles,
    UltsSoundFileKey,
    UltrasoundError,
    UltrasoundErrorType,
} from './types';
// Cache imports disabled - cache functionality is turned off
// import {
//     getCacheKey,
//     safeGetFromCache,
//     safeUpdateCache,
//     clearCache,
//     initializeCacheManagement,
//     shutdownCacheManagement
// } from './ultrasound-cache';

// Import only clearCache for the service interface
import { clearCache, initializeCacheManagement, shutdownCacheManagement } from './ultrasound-cache';

// Retry configuration for recoverable errors
const RETRY_CONFIG = {
    maxAttempts: 2, // Reduced from 3 to 2 to minimize excessive retries
    baseDelay: 1000, // 1 second
    maxDelay: 3000,  // Reduced from 5 to 3 seconds
    backoffMultiplier: 2
};

/**
 * Parsed position result interface
 */
interface ParsedPosition {
    asset: string;
    position: string;
}

/**
 * Parses position identifier to handle equipment ID prefixes
 * Position identifiers may contain equipment prefixes in format "equipmentId#position"
 * to avoid conflicts when the tree renders several equipments with the same position names
 * @param asset - Original asset identifier
 * @param position - Position identifier that may include equipment prefix
 * @returns Parsed asset and position identifiers
 * @throws UltrasoundError when position format is invalid
 */
const parsePosition = (asset: string, position: string): ParsedPosition => {
    try {
        if (position.includes('#')) {
            const parts = position.split('#');
            if (parts.length !== 2) {
                throw new UltrasoundError(
                    UltrasoundErrorType.INVALID_POSITION,
                    `Invalid position format: expected "equipmentId#position", got "${position}"`
                );
            }
            return {
                asset: parts[0],
                position: parts[1]
            };
        }

        // Return original values if no prefix found
        return { asset, position };
    } catch (error) {
        if (error instanceof UltrasoundError) {
            // Re-throw ultrasound errors
            throw error;
        }

        // Handle unexpected parsing errors
        throw new UltrasoundError(
            UltrasoundErrorType.INVALID_POSITION,
            `Position parsing failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
            undefined,
            error instanceof Error ? error : undefined
        );
    }
};

/**
 * Maps API errors to ultrasound-specific errors with appropriate fallback handling
 * @param error - The original error from API call
 * @param operation - The ultrasound operation that failed
 * @returns UltrasoundError with appropriate type and message
 */
const mapApiError = (error: unknown, operation: string): UltrasoundError => {
    if (error instanceof ApiError) {
        switch (error.type) {
            case ApiErrorType.NOT_FOUND:
                if (operation.includes('recommendation')) {
                    return new UltrasoundError(
                        UltrasoundErrorType.RECOMMENDATION_UNAVAILABLE,
                        'No baseline recommendations are available for this equipment position',
                        error.statusCode,
                        error
                    );
                } else if (operation.includes('baseline')) {
                    return new UltrasoundError(
                        UltrasoundErrorType.BASELINE_NOT_FOUND,
                        'No baseline data found for this equipment position',
                        error.statusCode,
                        error
                    );
                } else if (operation.includes('wav')) {
                    return new UltrasoundError(
                        UltrasoundErrorType.WAV_FILE_NOT_FOUND,
                        'WAV file not found or no longer available',
                        error.statusCode,
                        error
                    );
                }
                break;

            case ApiErrorType.NETWORK:
            case ApiErrorType.TIMEOUT:
                return new UltrasoundError(
                    UltrasoundErrorType.NETWORK_ERROR,
                    'Network connectivity issue. Please check your connection and try again.',
                    error.statusCode,
                    error
                );

            case ApiErrorType.SERVER:
                if (operation.includes('recommendation')) {
                    return new UltrasoundError(
                        UltrasoundErrorType.ML_SERVICE_ERROR,
                        'The recommendation service is temporarily unavailable. Please try again later.',
                        error.statusCode,
                        error
                    );
                }
                return new UltrasoundError(
                    UltrasoundErrorType.ML_SERVICE_ERROR,
                    'Server error occurred while processing ultrasound data. Please try again later.',
                    error.statusCode,
                    error
                );

            case ApiErrorType.UNAUTHORIZED:
            case ApiErrorType.FORBIDDEN:
                return new UltrasoundError(
                    UltrasoundErrorType.ML_SERVICE_ERROR,
                    'Access denied. Please check your permissions for ultrasound data.',
                    error.statusCode,
                    error
                );

            case ApiErrorType.VALIDATION:
                return new UltrasoundError(
                    UltrasoundErrorType.INVALID_POSITION,
                    'Invalid request parameters. Please verify the equipment position.',
                    error.statusCode,
                    error
                );
        }
    }

    // Handle non-API errors
    if (error instanceof Error) {
        return new UltrasoundError(
            UltrasoundErrorType.NETWORK_ERROR,
            `Unexpected error: ${error.message}`,
            undefined,
            error
        );
    }

    // Handle unknown errors
    return new UltrasoundError(
        UltrasoundErrorType.ML_SERVICE_ERROR,
        'An unknown error occurred while processing ultrasound data',
        undefined,
        undefined
    );
};

/**
 * Determines if an error is recoverable and should be retried
 * @param error - The error to check
 * @returns True if the error is recoverable, false otherwise
 */
const isRecoverableError = (error: unknown): boolean => {
    if (error instanceof UltrasoundError) {
        // Retry network errors, timeouts, and server errors
        return error.type === UltrasoundErrorType.NETWORK_ERROR || 
               error.type === UltrasoundErrorType.ML_SERVICE_ERROR;
    }
    
    if (error instanceof ApiError) {
        // Retry network, timeout, and server errors
        return error.type === ApiErrorType.NETWORK || 
               error.type === ApiErrorType.TIMEOUT || 
               error.type === ApiErrorType.SERVER;
    }
    
    return false;
};

/**
 * Implements exponential backoff delay for retry attempts
 * @param attempt - Current attempt number (0-based)
 * @returns Delay in milliseconds
 */
const calculateRetryDelay = (attempt: number): number => {
    const delay = RETRY_CONFIG.baseDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, attempt);
    return Math.min(delay, RETRY_CONFIG.maxDelay);
};

/**
 * Sleeps for the specified number of milliseconds
 * @param ms - Milliseconds to sleep
 */
const sleep = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Executes a function with retry logic for recoverable errors
 * @param operation - The async operation to execute
 * @param operationName - Name of the operation for error messages
 * @returns Promise resolving to the operation result
 */
const executeWithRetry = async <T>(
    operation: () => Promise<T>,
    operationName: string
): Promise<T> => {
    let lastError: unknown;
    
    for (let attempt = 0; attempt < RETRY_CONFIG.maxAttempts; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error;
            
            // Don't retry if error is not recoverable or if this is the last attempt
            if (!isRecoverableError(error) || attempt === RETRY_CONFIG.maxAttempts - 1) {
                throw error;
            }
            
            // Calculate delay and wait before retry
            const delay = calculateRetryDelay(attempt);
            console.warn(
                `${operationName} failed (attempt ${attempt + 1}/${RETRY_CONFIG.maxAttempts}), retrying in ${delay}ms:`,
                error
            );
            
            await sleep(delay);
        }
    }
    
    // This should never be reached, but throw the last error as fallback
    throw lastError;
};

/**
 * Ultrasound Service with methods for fetching ultrasound measurement data
 */
export const ultrasoundService = {

    async getSoundFiles(
        site: string,
        asset: string,
        position: string,
        startDate: string,
        endDate: string,
    ): Promise<UltsSoundFiles> {
        const operation = "ults-sound-files";

        try {
            // Parse position to handle equipment ID prefixes
            const parsedPosition = parsePosition(asset, position);

            // Execute API request with retry logic for recoverable errors
            const data = await executeWithRetry(async () => {
                const encodedAsset = encodeURIComponent(parsedPosition.asset);
                const encodedPosition = encodeURIComponent(parsedPosition.position);
                
                const response = await apiClient.get<UltsSoundFiles>(
                    `/${operation}`,
                    {
                        params: {
                            site,
                            asset: encodedAsset,
                            position: encodedPosition,
                            start_date: startDate,
                            end_date: endDate,
                        }
                    }
                );
                console.log(`[ultrasound-service.ts] getSoundFiles response: ${response}`);
                // Validate response data
                if (!response || !response.soundFiles) {
                    throw new UltrasoundError(
                        UltrasoundErrorType.INVALID_POSITION,
                        'Invalid position'
                    );
                }

                return response;
            }, `Sound Files data retrieval for ${site}/${parsedPosition.asset}/${parsedPosition.position}`);


            return data;
        } catch (error) {
            // Handle position parsing errors (already UltrasoundError)
            if (error instanceof UltrasoundError) {
                throw error;
            }

            // Map API errors to ultrasound-specific errors
            throw mapApiError(error, operation);
        }
    },

    /**
     * Retrieves AI-powered baseline recommendations for ultrasound measurements
     * @param site - Site identifier
     * @param asset - Asset identifier
     * @param position - Position identifier (may include equipment prefix)
     * @returns Promise resolving to baseline recommendation data
     * @throws UltrasoundError for various failure scenarios with specific error types
     */
    async getBaselineRecommendation(
        site: string,
        asset: string,
        position: string,
    ): Promise<UltsBaselineRecommendation> {
        const operation = "ults-baseline-recommendation";

        try {
            // Parse position to handle equipment ID prefixes
            const parsedPosition = parsePosition(asset, position);
            // Cache disabled - skip cache key generation and cache check
            // const cacheKey = getCacheKey(operation, site, parsedPosition.asset, parsedPosition.position);
            // const cachedData = safeGetFromCache<UltsBaselineRecommendation>(cacheKey, operation);
            // if (cachedData) {
            //     return cachedData;
            // }

            // Execute API request with retry logic for recoverable errors
            const data = await executeWithRetry(async () => {
                const encodedAsset = encodeURIComponent(parsedPosition.asset);
                const encodedPosition = encodeURIComponent(parsedPosition.position);
                
                const response = await apiClient.get<UltsBaselineRecommendation>(
                    `/${operation}`,
                    {
                        params: {
                            site,
                            asset: encodedAsset,
                            position: encodedPosition,
                        }
                    }
                );

                // Validate response data
                if (!response || typeof response.currentBaseline !== 'number' || typeof response.recommendation !== 'number') {
                    throw new UltrasoundError(
                        UltrasoundErrorType.ML_SERVICE_ERROR,
                        'Invalid recommendation data received from server'
                    );
                }

                return response;
            }, `Baseline recommendation retrieval for ${site}/${parsedPosition.asset}/${parsedPosition.position}`);

            // Cache disabled - skip cache update
            // safeUpdateCache(cacheKey, data, operation);

            return data;
        } catch (error) {
            // Handle position parsing errors (already UltrasoundError)
            if (error instanceof UltrasoundError) {
                throw error;
            }

            // Map API errors to ultrasound-specific errors
            throw mapApiError(error, operation);
        }
    },

    /**
     * Retrieves current baseline data for ultrasound measurements
     * @param site - Site identifier
     * @param asset - Asset identifier
     * @param position - Position identifier (may include equipment prefix)
     * @returns Promise resolving to baseline data with WAV file information
     * @throws UltrasoundError for various failure scenarios with specific error types
     */
    async getBaseline(
        site: string,
        asset: string,
        position: string,
    ): Promise<UltsBaseline> {
        const operation = "ults-baseline";

        try {
            // Parse position to handle equipment ID prefixes
            const parsedPosition = parsePosition(asset, position);
            // Cache disabled - skip cache key generation and cache check
            // const cacheKey = getCacheKey(operation, site, parsedPosition.asset, parsedPosition.position);
            // const cachedData = safeGetFromCache<UltsBaseline>(cacheKey, operation);
            // if (cachedData) {
            //     return cachedData;
            // }

            // Execute API request with retry logic for recoverable errors
            const data = await executeWithRetry(async () => {
                const encodedSite = encodeURIComponent(site);
                const encodedAsset = encodeURIComponent(parsedPosition.asset);
                const encodedPosition = encodeURIComponent(parsedPosition.position);
                
                const response = await apiClient.get<UltsBaseline>(
                    `/${operation}`,
                    {
                        params: {
                            site,
                            asset: encodedAsset,
                            position: encodedPosition,
                        }
                    }
                );
                console.log(`[ultrasound-service.ts] getBaseline response: ${response}`);
                // Validate response data
                if (!response || typeof response.baseline !== 'number' || !response.fileKey || !response.wavFileUrl) {
                    throw new UltrasoundError(
                        UltrasoundErrorType.BASELINE_NOT_FOUND,
                        'Invalid baseline data received from server'
                    );
                }

                return response;
            }, `Baseline data retrieval for ${site}/${parsedPosition.asset}/${parsedPosition.position}`);

            // Cache disabled - skip cache update
            // safeUpdateCache(cacheKey, data, operation);

            return data;
        } catch (error) {
            // Handle position parsing errors (already UltrasoundError)
            if (error instanceof UltrasoundError) {
                throw error;
            }

            // Map API errors to ultrasound-specific errors
            throw mapApiError(error, operation);
        }
    },

    /**
     * Retrieves WAV file URL for ultrasound audio data
     * @param site - Site identifier
     * @param wavId - WAV file identifier
     * @returns Promise resolving to WAV file download URL
     * @throws UltrasoundError for various failure scenarios with specific error types
     */
    async getWavFileUrl(
        site: string,
        wavId: string,
    ): Promise<string> {
        const operation = "ults-wav-url";

        try {
            // Cache disabled - skip cache key generation and cache check
            // const cacheKey = getCacheKey(operation, site, wavId);
            // const cachedData = safeGetFromCache<string>(cacheKey, operation);
            // if (cachedData) {
            //     return cachedData;
            // }

            // Execute API request with retry logic for recoverable errors
            const data = await executeWithRetry(async () => {
                const encodedWavId = encodeURIComponent(wavId);
                
                const response = await apiClient.get<{ wavFileUrl: string }>(
                    `/${operation}`,
                    {
                        params: {
                            site,
                            wav_id: encodedWavId,
                        }
                    }
                );

                // Validate response data
                if (!response || !response.wavFileUrl || typeof response.wavFileUrl !== 'string') {
                    throw new UltrasoundError(
                        UltrasoundErrorType.WAV_FILE_NOT_FOUND,
                        'Invalid WAV file URL received from server'
                    );
                }

                return response;
            }, `WAV file URL retrieval for ${site}/${wavId}`);

            // Cache disabled - skip cache update
            // safeUpdateCache(cacheKey, data.wavFileUrl, operation);

            return data.wavFileUrl;
        } catch (error) {
            // Handle ultrasound errors
            if (error instanceof UltrasoundError) {
                throw error;
            }

            // Map API errors to ultrasound-specific errors
            throw mapApiError(error, operation);
        }
    },

    /**
     * Clears ultrasound cache entries based on provided parameters
     * @param site - Optional site identifier to filter cache entries
     * @param asset - Optional asset identifier to filter cache entries  
     * @param position - Optional position identifier to filter cache entries
     * @throws UltrasoundError if cache clearing fails critically
     */
    clearCache,

    /**
     * Provides graceful fallback data when recommendations are unavailable
     * @param site - Site identifier
     * @param asset - Asset identifier
     * @param position - Position identifier
     * @returns Fallback recommendation data
     */
    getFallbackRecommendation(
        site: string,
        asset: string,
        position: string,
    ): UltsBaselineRecommendation {
        console.warn(`Fallback recommendation requested for ${site}/${asset}/${position}`);
        return {
            currentBaseline: 0,
            recommendation: 0,
            reason: 'Baseline recommendations are temporarily unavailable. Please try again later or contact support if the issue persists.'
        };
    },

    /**
     * Provides graceful fallback data when baseline data is unavailable
     * @param site - Site identifier
     * @param asset - Asset identifier
     * @param position - Position identifier
     * @returns Fallback baseline data
     */
    getFallbackBaseline(
        site: string,
        asset: string,
        position: string,
    ): UltsBaseline {
        console.warn(`Fallback baseline requested for ${site}/${asset}/${position}`);
        return {
            baseline: 0,
            fileKey: 'unavailable',
            wavFileUrl: ''
        };
    },

    /**
     * Initializes cache management (called automatically)
     */
    initializeCacheManagement,

    /**
     * Shuts down cache management intervals
     */
    shutdownCacheManagement
};

// Cache disabled - skip cache initialization
// initializeCacheManagement();